/**
 * "Where does this number come from?" — the ⓘ beside a figure.
 *
 *   import { explainButton, wireExplain } from '@isg/ui-kit';
 *   th.innerHTML = `Demand ${explainButton('purchasing.demand')}`;
 *   td.innerHTML = `${qty} ${explainButton('purchasing.action', { warehouse, part })}`;
 *   wireExplain(container);           // once, on the page's root
 *
 * Reads the app's `/api/figures` (isg_common.figures). Two tabs:
 *
 *   What it means     — the definition: one sentence, the method as steps,
 *                       what it is built from and when that was last copied
 *                       from QX, and what people get wrong about it.
 *   Show the working  — for ONE value, when the button carries params: the
 *                       book's own arithmetic for that row, from the server.
 *   Ask about this    — when the app offers it (`askable`): a question about
 *                       this figure (and this row's working), answered by the
 *                       app's LOCAL model — never an outside AI. The server
 *                       withholds an answer quoting a figure it was not given.
 *
 * ⚠️ Nothing here is generated. The definition is the app's registry and the
 * working is the code that produced the number — the same entry the app's AI
 * assistant reads, so the page and the assistant cannot tell two stories.
 * Programme and rules: /opt/isg/isg-common/docs/figures/.
 *
 * ⚠️ The button stops its click from bubbling: it usually sits inside a
 * sortable table header, and opening an explanation must not re-sort the
 * table underneath it.
 */
import '../../css/explain.css';
import { api, localeHeaders } from '../services/api.js';
import { escapeHtml } from '../utils/helpers.js';
import { getIcon } from './navigation.js';
import { showModal } from './modal.js';
import { getLocale, t } from '../i18n/index.js';

/* Built per call, in the reader's locale (never at module scope — the locale
 * is not known until the app has booted i18n). A unit cost keeps the column's
 * scale (6) — rounding one to pounds renders real costs as £0
 * (isg-purchasing DO #40). */
function numberFormat(type) {
  return new Intl.NumberFormat(getLocale(), { maximumFractionDigits: type === 'unit_cost' ? 6 : 2 });
}

function fmt(v, type) {
  if (v === null || v === undefined) return '<span class="fig-unknown">—</span>';
  if (typeof v === 'number') return escapeHtml(numberFormat(type).format(v));
  if (typeof v === 'boolean') return v ? escapeHtml(t('explain.yes')) : '';
  return escapeHtml(String(v));
}

function day(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString(getLocale(), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** The ⓘ, as HTML. `params` identify ONE value (a row); omit for a header. */
export function explainButton(figureKey, params = null, { label = null } = {}) {
  if (label == null) label = t('explain.how_worked_out');
  const p = params ? ` data-fig-params="${escapeHtml(JSON.stringify(params))}"` : '';
  return `<button type="button" class="fig-info" data-figure="${escapeHtml(figureKey)}"${p}
    title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${getIcon('info')}</button>`;
}

/** Delegate every ⓘ under `root`. Safe to call once per page mount. */
export function wireExplain(root) {
  if (!root || root.__figWired) return;
  root.__figWired = true;
  const open = (btn, e) => {
    e.preventDefault();
    e.stopPropagation();
    let params = null;
    try { params = btn.dataset.figParams ? JSON.parse(btn.dataset.figParams) : null; } catch { params = null; }
    openExplain(btn.dataset.figure, params);
  };
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('.fig-info');
    if (btn && root.contains(btn)) open(btn, e);
  }, true);
  root.addEventListener('keydown', (e) => {
    const btn = e.target.closest && e.target.closest('.fig-info');
    if (btn && (e.key === 'Enter' || e.key === ' ')) open(btn, e);
  }, true);
}

function sourceHtml(s) {
  let when;
  if (s.name.startsWith('app:')) when = '';
  else if (s.known) when = `<span class="fig-when">${escapeHtml(t('explain.last_copied', { when: day(s.last_synced) || s.last_synced }))}</span>`;
  else when = `<span class="fig-when fig-unknown">${escapeHtml(t('explain.last_copy_unknown'))}</span>`;
  return `<li>${escapeHtml(s.label)} ${when}</li>`;
}

function definitionHtml(d) {
  const list = (items) => items.map((x) => `<li>${escapeHtml(x)}</li>`).join('');
  return `
    <p class="fig-means">${escapeHtml(d.means)}</p>
    <h4 class="fig-h">${escapeHtml(t('explain.how_heading'))}</h4>
    <ol class="fig-how">${list(d.how)}</ol>
    ${d.sources.length ? `
      <h4 class="fig-h">${escapeHtml(t('explain.built_from'))}</h4>
      <ul class="fig-sources">${d.sources.map(sourceHtml).join('')}</ul>
      ${d.sources.some((s) => !s.name.startsWith('app:'))
        ? `<p class="fig-note">${escapeHtml(t('explain.last_copied_note'))}</p>`
        : ''}` : ''}
    ${d.traps.length ? `
      <h4 class="fig-h">${escapeHtml(t('explain.watch_out'))}</h4>
      <ul class="fig-traps">${list(d.traps)}</ul>` : ''}
    ${d.concept && d.concept.caveat ? `<p class="fig-note">${escapeHtml(d.concept.caveat)}</p>` : ''}`;
}

function workingHtml(w) {
  const steps = (w.steps || []).map((s) => `
    <tr class="${s.label.startsWith('=') ? 'fig-total' : ''}">
      <td>${escapeHtml(s.label)}${s.note ? `<div class="fig-sub">${escapeHtml(s.note)}</div>` : ''}</td>
      <td class="fig-num">${fmt(s.value)}</td>
    </tr>`).join('');
  const cols = w.columns || [];
  const lines = (w.lines || []).length && cols.length ? `
    <div class="table-responsive"><table class="data-table fig-lines">
      <thead><tr>${cols.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${w.lines.map((l) => `<tr class="${l.used ? 'fig-used' : ''}">${
        cols.map((c) => `<td>${fmt(l[c.key], c.type)}</td>`).join('')}</tr>`).join('')}</tbody>
    </table></div>` : '';
  return `
    <p class="fig-means">${t('explain.this_value', { value: `<strong>${fmt(w.value)}</strong>` })}</p>
    ${steps ? `<div class="table-responsive"><table class="data-table fig-steps"><tbody>${steps}</tbody></table></div>` : ''}
    ${lines}
    ${(w.notes || []).map((n) => `<p class="fig-note">${escapeHtml(n)}</p>`).join('')}
    ${w.as_of ? `<p class="fig-note">${escapeHtml(t('explain.worked_from_book', { when: day(w.as_of) || w.as_of }))}</p>` : ''}`;
}

// ── Ask about this ──────────────────────────────────────────────────────────

/* Keys, not text: translated when the panel is drawn. */
const ASK_SUGGESTIONS = ['plain_english', 'why_value', 'what_changes', 'watch_out']; // i18n-ignore: key suffixes, not text

function readCookie(name) {
  const hit = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : '';
}

function apiBase() {
  if (typeof window !== 'undefined' && window.__API_BASE__) return window.__API_BASE__;
  const b = typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL;
  return b ? `${b.replace(/\/$/, '')}/api` : '/api';
}

/** POST and read the server-sent events. Same auth as the api client. */
async function* askStream(key, body, signal) {
  const url = `${apiBase()}/figures/${key}/ask`;
  // The reader's language, so the answer (and any refusal) comes back in it.
  const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...localeHeaders(url) };
  const csrf = readCookie('csrf_access_token');
  if (csrf) headers['X-CSRF-TOKEN'] = csrf;
  if (api._token) headers.Authorization = `Bearer ${api._token}`;
  const res = await fetch(url, {
    method: 'POST', credentials: 'include', headers, body: JSON.stringify(body), signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error(`ask failed (${res.status})`); // i18n-ignore: internal; the catch shows a translated message by status
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let cut;
    // A chunk can end half-way through a frame: keep the remainder.
    while ((cut = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      let event = 'message';
      let data = '';
      frame.split('\n').forEach((l) => {
        if (l.startsWith('event: ')) event = l.slice(7).trim();
        else if (l.startsWith('data: ')) data += l.slice(6);
      });
      try { yield { event, data: data ? JSON.parse(data) : {} }; } catch { /* skip a bad frame */ }
    }
  }
}

function askPanel(key, params) {
  const el = document.createElement('div');
  el.className = 'fig-ask';
  el.innerHTML = `
    <p class="fig-note fig-ask-where">${getIcon('lock')} ${escapeHtml(t('explain.ask.where'))}</p>
    <div class="fig-ask-thread" aria-live="polite"></div>
    <div class="fig-ask-suggest">${ASK_SUGGESTIONS.map((k) => {
      const q = t(`explain.ask.suggest.${k}`);
      return `<button type="button" class="btn btn-sm btn-secondary" data-ask-q="${escapeHtml(q)}">${escapeHtml(q)}</button>`;
    }).join('')}</div>
    <form class="fig-ask-form">
      <textarea class="form-input" rows="2" maxlength="1500" placeholder="${escapeHtml(t('explain.ask.placeholder'))}"
        aria-label="${escapeHtml(t('explain.ask.your_question'))}"></textarea>
      <button type="submit" class="btn btn-primary btn-sm">${escapeHtml(t('explain.ask.send'))}</button>
    </form>`;
  const thread = el.querySelector('.fig-ask-thread');
  const form = el.querySelector('form');
  const box = form.querySelector('textarea');
  const send = form.querySelector('button');
  const history = [];
  let busy = false;

  const bubble = (role, text) => {
    const b = document.createElement('div');
    b.className = `fig-ask-msg fig-ask-${role}`;
    b.textContent = text;
    thread.appendChild(b);
    b.scrollIntoView({ block: 'nearest' });
    return b;
  };

  async function ask(question) {
    const q = question.trim();
    if (!q || busy) return;
    busy = true;
    send.disabled = true;
    box.value = '';
    el.querySelector('.fig-ask-suggest').hidden = true;
    history.push({ role: 'user', content: q });
    bubble('user', q);
    const out = bubble('assistant', t('explain.ask.thinking'));
    out.classList.add('fig-ask-pending');
    let text = '';
    let ended = false;
    try {
      for await (const { event, data } of askStream(key, { messages: history, params })) {
        if (event === 'delta') {
          if (!text) out.classList.remove('fig-ask-pending');
          text += data.text || '';
          out.textContent = text;
        } else if (event === 'done') {
          ended = true;
          out.textContent = (data.text || text).trim()
            + (data.truncated ? ` … ${t('explain.ask.cut_short')}` : '');
          history.push({ role: 'assistant', content: out.textContent });
        } else if (event === 'withheld' || event === 'error') {
          ended = true;
          out.classList.remove('fig-ask-pending');
          out.classList.add('fig-ask-problem');
          out.textContent = data.message || t('explain.ask.no_answer');
          history.pop();                 // the question was not answered
        }
      }
      if (!ended) throw new Error('stream ended early'); // i18n-ignore: internal; the catch shows a translated message
    } catch (err) {
      out.classList.remove('fig-ask-pending');
      out.classList.add('fig-ask-problem');
      out.textContent = err?.status === 429
        ? t('explain.ask.rate_limited')
        : err?.status === 404 ? t('explain.ask.row_gone')
          : t('explain.ask.failed');
      if (history.length && history[history.length - 1].role === 'user') history.pop();
    } finally {
      busy = false;
      send.disabled = false;
      box.focus();
    }
  }

  form.addEventListener('submit', (e) => { e.preventDefault(); ask(box.value); });
  box.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(box.value); }
  });
  el.querySelectorAll('[data-ask-q]').forEach((b) =>
    b.addEventListener('click', () => ask(b.dataset.askQ)));
  return el;
}

/** Open the explanation for one figure (and optionally one value of it). */
export async function openExplain(figureKey, params = null) {
  const key = encodeURIComponent(figureKey);
  let d;
  try {
    d = await api.get(`/figures/${key}`);
  } catch (err) {
    showModal({ title: escapeHtml(t('explain.how_worked_out')),
      content: `<p>${escapeHtml(err?.status === 404
        ? t('explain.not_found')
        : t('explain.load_failed'))}</p>` });
    return;
  }
  const withWorking = !!(params && d.explainable);
  const askable = !!d.askable;
  const body = document.createElement('div');
  body.className = 'fig-explain';
  body.innerHTML = `
    ${withWorking || askable ? `
      <div class="tab-bar" role="tablist">
        <button type="button" class="tab-item active" data-fig-tab="def" role="tab" aria-selected="true">${escapeHtml(t('explain.tab.means'))}</button>
        ${withWorking ? `<button type="button" class="tab-item" data-fig-tab="work" role="tab" aria-selected="false">${escapeHtml(t('explain.tab.working'))}</button>` : ''}
        ${askable ? `<button type="button" class="tab-item" data-fig-tab="ask" role="tab" aria-selected="false">${escapeHtml(t('explain.tab.ask'))}</button>` : ''}
      </div>` : ''}
    <div data-fig-panel="def">${definitionHtml(d)}</div>
    ${withWorking ? `<div data-fig-panel="work" hidden><p class="fig-note">${escapeHtml(t('explain.loading'))}</p></div>` : ''}
    ${askable ? '<div data-fig-panel="ask" hidden></div>' : ''}`;
  if (askable) {
    // The row's params go with the question, so the server re-reads the
    // working itself (inside the reader's reach) rather than trusting ours.
    body.querySelector('[data-fig-panel="ask"]').appendChild(
      askPanel(key, withWorking ? params : null));
  }

  let loaded = false;
  const loadWorking = async () => {
    if (loaded) return;
    loaded = true;
    const panel = body.querySelector('[data-fig-panel="work"]');
    try {
      panel.innerHTML = workingHtml(await api.get(`/figures/${key}/explain`, params));
    } catch (err) {
      loaded = false;
      panel.innerHTML = `<p class="fig-note">${escapeHtml(err?.status === 404
        ? t('explain.working_not_found')
        : t('explain.working_failed'))}</p>`;
    }
  };
  body.querySelectorAll('[data-fig-tab]').forEach((tab) => {
    tab.addEventListener('click', () => {
      body.querySelectorAll('[data-fig-tab]').forEach((el) => {
        const on = el === tab;
        el.classList.toggle('active', on);
        el.setAttribute('aria-selected', String(on));
      });
      body.querySelectorAll('[data-fig-panel]').forEach((p) => {
        p.hidden = p.dataset.figPanel !== tab.dataset.figTab;
      });
      if (tab.dataset.figTab === 'work') loadWorking();
    });
  });
  showModal({ title: escapeHtml(d.label), content: body, size: 'md' });
}
